// All pricing math for 360 Nails Price Calculator. Pure functions, no side effects.

export function num(v) {
  const n = typeof v === "string" ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : 0;
}

export function materialCost(m) {
  if (!m) return 0;
  if (m.method === "simple") return Math.max(0, num(m.direct_cost));
  const pkgQty = num(m.package_quantity);
  if (pkgQty <= 0) return 0;
  const unitCost = num(m.package_price) / pkgQty;
  return Math.max(0, unitCost * num(m.quantity_used));
}

export function suggestCommercial(value) {
  const v = num(value);
  if (v <= 0) return { rounded: 0, nice: 0 };
  const rounded = Math.round(v);
  const nice = Math.ceil(v / 5) * 5; // round up to nearest 5
  return { rounded, nice };
}

// Full computation for a service using the user's profile settings.
export function computeService(service, profile) {
  const duration = Math.max(0, num(service.duration_min));
  const extraPct = Math.max(0, num(service.extra_time_pct));
  const hourly = Math.max(0, num(profile.hourly_value));
  const vatEnabled = !!profile.vat_enabled;
  const vatRate = Math.max(0, num(profile.vat_rate));

  const effectiveMin = duration * (1 + extraPct / 100);
  const extraMin = effectiveMin - duration;
  const timeCost = (effectiveMin / 60) * hourly;

  const materials = Array.isArray(service.materials) ? service.materials : [];
  const totalMaterial = materials.reduce((s, m) => s + materialCost(m), 0);

  const baseCost = totalMaterial + timeCost;

  const margin = Math.min(99, Math.max(0, num(service.desired_margin)));
  const recommendedNet = margin < 100 ? baseCost / (1 - margin / 100) : baseCost;
  const vatAmount = vatEnabled ? (recommendedNet * vatRate) / 100 : 0;
  const finalPrice = recommendedNet + vatAmount;
  const commercial = suggestCommercial(finalPrice);

  // Current price comparison
  let current = null;
  const cpRaw = service.current_price;
  if (cpRaw !== null && cpRaw !== undefined && cpRaw !== "") {
    const cp = Math.max(0, num(cpRaw));
    if (cp > 0) {
      const currentNet = vatEnabled ? cp / (1 + vatRate / 100) : cp;
      const currentMargin = currentNet > 0 ? ((currentNet - baseCost) / currentNet) * 100 : 0;
      current = {
        price: cp,
        net: currentNet,
        margin: currentMargin,
        difference: cp - finalPrice,
      };
    }
  }

  return {
    duration, extraPct, effectiveMin, extraMin, timeCost,
    totalMaterial, baseCost, margin, recommendedNet,
    vatEnabled, vatRate, vatAmount, finalPrice, commercial, current, hourly,
  };
}

// Rule-based smart result. Returns { status, messageKey, params, suggestions[] }
export function smartResult(calc) {
  if (!calc.current) {
    return { status: "info", messageKey: "smart.noPrice", suggestions: [] };
  }
  const { current, baseCost, margin, timeCost, totalMaterial, hourly, recommendedNet, vatEnabled, vatRate } = calc;
  const targetMargin = margin;

  // Selling price to reach target margin at final (customer) level
  const suggestions = [];

  // Suggestion: increase price
  if (calc.finalPrice > current.price + 0.01) {
    suggestions.push({ key: "smart.increaseTo", type: "price", value: calc.commercial.nice || calc.finalPrice });
  }

  // Needed base cost so that current net reaches target margin
  const neededBase = current.net * (1 - targetMargin / 100);
  // Reduce time
  const neededTimeCost = neededBase - totalMaterial;
  if (neededTimeCost >= 0 && neededTimeCost < timeCost - 0.01 && hourly > 0) {
    const minReduce = ((timeCost - neededTimeCost) / hourly) * 60;
    if (minReduce >= 1) suggestions.push({ key: "smart.reduceTimeBy", type: "time", value: Math.round(minReduce) });
  }
  // Reduce material
  const neededMaterial = neededBase - timeCost;
  if (neededMaterial >= 0 && neededMaterial < totalMaterial - 0.01) {
    suggestions.push({ key: "smart.reduceMaterialBy", type: "material", value: totalMaterial - neededMaterial });
  }

  if (current.net < baseCost - 0.01) {
    return { status: "danger", messageKey: "smart.belowCost", suggestions };
  }
  if (current.margin < targetMargin - 0.5) {
    return { status: "warning", messageKey: "smart.lowMargin", suggestions };
  }
  return { status: "success", messageKey: "smart.healthy", suggestions: [] };
}

// Break-even: how many services to cover monthly fixed costs.
export function breakEven(calc, fixedTotal) {
  const contribution = calc.recommendedNet - calc.baseCost;
  if (contribution <= 0 || fixedTotal <= 0) return null;
  return Math.ceil(fixedTotal / contribution);
}

// Monthly simulation across services with quantities map { serviceId: qty }
export function simulateMonth(services, quantities, profile, fixedTotal) {
  let totalServices = 0, gross = 0, net = 0, material = 0, timeValue = 0, effectiveMin = 0;
  services.forEach((s) => {
    const q = Math.max(0, num(quantities[s.id]));
    if (q <= 0) return;
    const c = computeService(s, profile);
    // Use current price if set, otherwise recommended final price
    const finalP = c.current ? c.current.price : c.finalPrice;
    const netP = c.current ? c.current.net : c.recommendedNet;
    totalServices += q;
    gross += finalP * q;
    net += netP * q;
    material += c.totalMaterial * q;
    timeValue += c.timeCost * q;
    effectiveMin += c.effectiveMin * q;
  });
  const vat = gross - net;
  const contribution = net - material - timeValue;
  const operatingResult = contribution - fixedTotal;
  return {
    totalServices, gross, net, vat, material, timeValue, contribution,
    fixedTotal, operatingResult, workingHours: effectiveMin / 60,
  };
}
